/* <fidno-card> — option state for the FIDNO product card overlay.
 *
 * Resolves the variant from the swatch/size radios, keeps the product form's
 * [name=id] in sync, updates add-to-cart availability, size-chip availability
 * for the picked color, and swaps the visible thumbnail to the picked color's
 * variant image (standard variant-image behavior). Cart submission itself is
 * Dawn's <product-form> — this element never fetches.
 */

if (!customElements.get('fidno-card')) {
  customElements.define(
    'fidno-card',
    class FidnoCard extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-fidno-card-data]');
        if (!dataEl) return;
        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (error) {
          return;
        }

        this.idInput = this.querySelector('form [name="id"]');
        this.atcButton = this.querySelector('.fidno-card__atc');
        this.atcLabel = this.atcButton ? this.atcButton.querySelector('span') : null;

        /* color slots start [hidden] so nothing flashes pre-JS; from here on
           visibility is opacity-driven (see fidno-product-card.css) */
        this.querySelectorAll('.fidno-card__color-slot').forEach((slot) => slot.removeAttribute('hidden'));

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-fidno-option]')) this.sync(event.target);
        });

        this.sync(null);
      }

      selectedOptions() {
        const selected = {};
        this.querySelectorAll('[data-fidno-option]:checked').forEach((input) => {
          selected[input.dataset.fidnoOption] = input.value;
        });
        return selected;
      }

      /* variant whose options match every currently selected option */
      resolveVariant(selected) {
        return this.data.variants.find((variant) =>
          this.data.options.every((name, index) => !(name in selected) || variant.opts[index] === selected[name])
        );
      }

      sync(changedInput) {
        const selected = this.selectedOptions();
        const variant = this.resolveVariant(selected);

        if (variant && this.idInput) this.idInput.value = variant.id;

        if (this.atcButton && this.atcLabel && window.variantStrings) {
          if (!variant) {
            this.atcButton.disabled = true;
            this.atcLabel.textContent = window.variantStrings.unavailable;
          } else if (!variant.available) {
            this.atcButton.disabled = true;
            this.atcLabel.textContent = window.variantStrings.soldOut;
          } else {
            this.atcButton.disabled = false;
            this.atcLabel.textContent = window.variantStrings.addToCart;
          }
        }

        this.updateSizeAvailability(selected);

        if (!changedInput || changedInput.dataset.fidnoOption === 'color' || changedInput.dataset.fidnoOption === 'colour') {
          this.updateColorImage(selected.color || selected.colour);
        }
      }

      /* a size stays enabled only if some available variant matches it plus
         the currently selected color */
      updateSizeAvailability(selected) {
        const sizeIndex = this.data.options.indexOf('size');
        if (sizeIndex === -1) return;
        const colorKey = 'color' in selected ? 'color' : 'colour' in selected ? 'colour' : null;

        this.querySelectorAll('[data-fidno-option="size"]').forEach((input) => {
          const match = this.data.variants.some(
            (variant) =>
              variant.available &&
              variant.opts[sizeIndex] === input.value &&
              (!colorKey || variant.opts[this.data.options.indexOf(colorKey)] === selected[colorKey])
          );
          input.disabled = !match;
        });
      }

      updateColorImage(color) {
        const slots = this.querySelectorAll('.fidno-card__color-slot');
        if (!slots.length) return;
        let active = null;
        slots.forEach((slot) => {
          const isActive = color && slot.dataset.fidnoColorImage === color;
          slot.toggleAttribute('data-fidno-color-active', isActive);
          if (isActive) active = slot;
        });
        this.classList.toggle('fidno-card--color-active', !!active);
      }
    }
  );
}
